import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { SeasonService } from '@/services/season-service';
import { daysUntilOpener, seasonStatus, SeasonStatus } from '@/utils/countdown-utils';
import type { Season, CreateSeasonInput, UpdateSeasonInput } from '@/types/season';

export { daysUntilOpener, seasonStatus };
export type { SeasonStatus };

const seasonService = new SeasonService(db);

interface SeasonStore {
  seasons: Season[];
  isLoading: boolean;
  /** True once the first loadSeasons() call has completed (success or error). */
  seasonsLoaded: boolean;
  error: string | null;

  loadSeasons: () => Promise<void>;
  getSeason: (id: string) => Season | undefined;
  getActiveSeasons: () => Season[];
  /** Returns the season with isPrimary=true, or falls back to the most recently created active season. */
  primarySeason: () => Season | undefined;
  addSeason: (data: CreateSeasonInput) => Promise<Season>;
  editSeason: (id: string, data: UpdateSeasonInput) => Promise<Season>;
  removeSeason: (id: string) => Promise<void>;
  setPrimary: (seasonId: string) => Promise<void>;
}

export const useSeasonStore = create<SeasonStore>((set, get) => ({
  seasons: [],
  isLoading: false,
  seasonsLoaded: false,
  error: null,

  loadSeasons: async () => {
    set({ isLoading: true, error: null });
    try {
      const seasons = await seasonService.getSeasons();
      // Sort by opener date: nearest upcoming first, then future, then past
      const sorted = [...seasons].sort((a, b) => a.openerDate.localeCompare(b.openerDate));
      set({ seasons: sorted, isLoading: false, seasonsLoaded: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load seasons';
      console.error('SeasonStore.loadSeasons:', err);
      set({ error: message, isLoading: false, seasonsLoaded: true });
    }
  },

  getSeason: (id) => get().seasons.find((s) => s.id === id),

  getActiveSeasons: () => {
    const today = new Date().toISOString().slice(0, 10);
    return get().seasons.filter(
      (s) => s.openerDate <= today && (!s.closerDate || s.closerDate >= today),
    );
  },

  primarySeason: () => {
    const { seasons } = get();
    const flagged = seasons.find((s) => s.isPrimary);
    if (flagged) return flagged;
    // Fall back: most recently created active or upcoming season
    const today = new Date().toISOString().slice(0, 10);
    const candidates = seasons.filter((s) => !s.closerDate || s.closerDate >= today);
    return candidates.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  },

  addSeason: async (data) => {
    try {
      const season = await seasonService.createSeason(data);
      set((state) => ({
        seasons: [...state.seasons, season].sort((a, b) =>
          a.openerDate.localeCompare(b.openerDate),
        ),
      }));
      return season;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create season';
      console.error('SeasonStore.addSeason:', err);
      set({ error: message });
      throw err;
    }
  },

  editSeason: async (id, data) => {
    try {
      const season = await seasonService.updateSeason(id, data);
      set((state) => ({
        seasons: state.seasons
          .map((s) => (s.id === id ? season : s))
          .sort((a, b) => a.openerDate.localeCompare(b.openerDate)),
      }));
      return season;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update season';
      console.error('SeasonStore.editSeason:', err);
      set({ error: message });
      throw err;
    }
  },

  removeSeason: async (id) => {
    try {
      await seasonService.deleteSeason(id);
      set((state) => ({ seasons: state.seasons.filter((s) => s.id !== id) }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete season';
      console.error('SeasonStore.removeSeason:', err);
      set({ error: message });
      throw err;
    }
  },

  setPrimary: async (seasonId) => {
    try {
      await db.setPrimarySeasons(seasonId);
      set((state) => ({
        seasons: state.seasons.map((s) => ({ ...s, isPrimary: s.id === seasonId })),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set primary season';
      console.error('SeasonStore.setPrimary:', err);
      set({ error: message });
      throw err;
    }
  },
}));
